import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { MultiSelectTags } from "./ui/multi-select-tags";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MapPin,
  Image as ImageIcon,
  Star,
  Calendar,
  User,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import type { Id } from "../../convex/_generated/dataModel";
import { toUrlSafeSlug } from "../../convex/lib/slug";
import { ImageDisplay, ImageGallery } from "./ui/image-display";

interface PropertyDialogProps {
  mode: "view" | "edit" | "create";
  property?: any;
  open: boolean;
  onClose: () => void;
}

interface PropertyFormData {
  property_name: string;
  property_name_idn: string;
  description: string;
  description_idn: string;
  reff_id: string;
  latitude: string;
  longitude: string;
  map_url: string;
  area_id?: Id<"area">;
  slug: string;
  featured: boolean;
  draft: boolean;
  hidden: boolean;
  tags: Id<"tag">[];
}

export function PropertyDialog({
  mode,
  property,
  open,
  onClose,
}: PropertyDialogProps) {
  const [formData, setFormData] = useState<PropertyFormData>({
    property_name: "",
    property_name_idn: "",
    description: "",
    description_idn: "",
    reff_id: "",
    latitude: "",
    longitude: "",
    map_url: "",
    slug: "",
    featured: false,
    draft: true,
    hidden: false,
    tags: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );

  // keyboard navigation for image gallery
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (selectedImageIndex === null) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          setSelectedImageIndex(
            selectedImageIndex > 0
              ? selectedImageIndex - 1
              : property.images.length - 1
          );
          break;
        case "ArrowRight":
          event.preventDefault();
          setSelectedImageIndex(
            selectedImageIndex < property.images.length - 1
              ? selectedImageIndex + 1
              : 0
          );
          break;
        case "Escape":
          event.preventDefault();
          setSelectedImageIndex(null);
          break;
      }
    },
    [selectedImageIndex, property?.images?.length]
  );

  // add/remove keyboard event listeners
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [selectedImageIndex, handleKeyDown]);

  // convex queries
  const areas = useQuery(api.contacts.getAllAreas, {});
  const tags = useQuery(api.tags.getAllTags, {});
  const currentUser = useQuery(api.users.current);
  const propertyWithRelations = useQuery(
    api.properties.getPropertyWithRelations,
    property?._id
      ? { id: property._id, showDrafts: true, showHidden: true }
      : "skip"
  );

  // get area data for the property
  const propertyArea = useQuery(
    api.contacts.getAreaById,
    property?.area_id ? { id: property.area_id } : "skip"
  );

  // get image keys for property images
  const imageKeys = property?.images?.map((img: any) => img.key) || [];

  // convex mutations
  const createProperty = useMutation(api.properties.createBaseProperty);
  const updateProperty = useMutation(api.properties.updateBaseProperty);

  // initialize form data
  useEffect(() => {
    if (mode === "create") {
      setFormData({
        property_name: "",
        property_name_idn: "",
        description: "",
        description_idn: "",
        reff_id: "",
        latitude: "",
        longitude: "",
        map_url: "",
        slug: "",
        featured: false,
        draft: true,
        hidden: false,
        tags: [],
      });
    } else if (property) {
      setFormData({
        property_name: property.property_name || "",
        property_name_idn: property.property_name_idn || "",
        description: property.description || "",
        description_idn: property.description_idn || "",
        reff_id: property.reff_id || "",
        latitude: property.latitude || "",
        longitude: property.longitude || "",
        map_url: property.map_url || "",
        area_id: property.area_id,
        slug: property.slug || "",
        featured: property.featured || false,
        draft: property.draft || false,
        hidden: property.hidden || false,
        tags: property.tags || [],
      });
    }
  }, [mode, property]);

  const handleInputChange = (field: keyof PropertyFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // auto-generate slug from property name
    if (field === "property_name" && typeof value === "string") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.property_name || !formData.description || !formData.reff_id) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        await createProperty({
          ...formData,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          area_id:
            formData.area_id ||
            ("j578fhw0r2w1f4ysym6h1kfdys7mbzgj" as Id<"area">), // default "Not Set" area
          created_by: currentUser?.email || "system",
        });
      } else if (mode === "edit" && property) {
        await updateProperty({
          id: property._id,
          ...formData,
          latitude: formData.latitude
            ? parseFloat(formData.latitude)
            : undefined,
          longitude: formData.longitude
            ? parseFloat(formData.longitude)
            : undefined,
          updated_by: currentUser?.email || "system",
        });
      }
      onClose();
    } catch (error) {
      console.error("Failed to save property:", error);
      alert("Failed to save property. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  const isReadOnly = mode === "view";
  const title =
    mode === "create"
      ? "Add New Property"
      : mode === "edit"
        ? "Edit Property"
        : "Property Details";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* basic information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="property_name">Property Name *</Label>
                  <Input
                    id="property_name"
                    value={formData.property_name}
                    onChange={(e) =>
                      handleInputChange("property_name", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="Enter property name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="property_name_idn">
                    Property Name (Indonesian)
                  </Label>
                  <Input
                    id="property_name_idn"
                    value={formData.property_name_idn}
                    onChange={(e) =>
                      handleInputChange("property_name_idn", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="Enter Indonesian name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  disabled={isReadOnly}
                  placeholder="Enter property description"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description_idn">
                  Description (Indonesian)
                </Label>
                <Textarea
                  id="description_idn"
                  value={formData.description_idn}
                  onChange={(e) =>
                    handleInputChange("description_idn", e.target.value)
                  }
                  disabled={isReadOnly}
                  placeholder="Enter Indonesian description"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reff_id">Reference ID *</Label>
                  <Input
                    id="reff_id"
                    value={formData.reff_id}
                    onChange={(e) =>
                      handleInputChange("reff_id", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="PROP-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      handleInputChange("slug", toUrlSafeSlug(e.target.value))
                    }
                    disabled={isReadOnly}
                    placeholder="property-name-slug"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* location information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="area_id">Area</Label>
                <Combobox
                  value={formData.area_id || ""}
                  onValueChange={(value) =>
                    handleInputChange("area_id", value || undefined)
                  }
                  disabled={isReadOnly}
                  placeholder="Select an area"
                  searchPlaceholder="Search areas..."
                  emptyText="No areas found"
                  options={
                    areas?.map((area) => ({
                      value: area._id,
                      label: `${area.area_name} - ${area.province}, ${area.country}`,
                    })) || []
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    value={formData.latitude}
                    onChange={(e) =>
                      handleInputChange("latitude", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="-8.648894"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    value={formData.longitude}
                    onChange={(e) =>
                      handleInputChange("longitude", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="115.137176"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="map_url">Map URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="map_url"
                    value={formData.map_url}
                    onChange={(e) =>
                      handleInputChange("map_url", e.target.value)
                    }
                    disabled={isReadOnly}
                    placeholder="https://maps.google.com/..."
                    className="flex-1"
                  />
                  {formData.map_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(formData.map_url, "_blank")}
                      type="button"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="tags">Property Tags</Label>
                <MultiSelectTags
                  tags={tags || []}
                  selectedTagIds={formData.tags}
                  onSelectionChange={(selectedIds) =>
                    handleInputChange("tags", selectedIds)
                  }
                  placeholder="Select tags for this property..."
                  searchPlaceholder="Search tags..."
                  emptyText="No tags found."
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>

          {/* status and settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status & Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) =>
                      handleInputChange("featured", e.target.checked)
                    }
                    disabled={isReadOnly}
                    className="rounded"
                  />
                  <span className="text-sm">Featured Property</span>
                  <Star className="h-4 w-4 text-yellow-500" />
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.draft}
                    onChange={(e) =>
                      handleInputChange("draft", e.target.checked)
                    }
                    disabled={isReadOnly}
                    className="rounded"
                  />
                  <span className="text-sm">Draft (not published)</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.hidden}
                    onChange={(e) =>
                      handleInputChange("hidden", e.target.checked)
                    }
                    disabled={isReadOnly}
                    className="rounded"
                  />
                  <span className="text-sm">Hidden from search</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* images and metadata (view mode only) */}
          {mode === "view" && propertyWithRelations && (
            <>
              {property?.images?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      Images ({property.images.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ImageGallery
                      images={property.images.map((img: any) => ({
                        key: img.key,
                        blurhash: img.blurhash,
                        filename: img.filename,
                      }))}
                      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                      onImageClick={(key, index) =>
                        setSelectedImageIndex(index)
                      }
                    />
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {property.images.map((image: any, index: number) => (
                        <div
                          key={index}
                          className="text-xs text-muted-foreground space-y-1"
                        >
                          <p className="truncate font-medium">
                            {image.filename}
                          </p>
                          {image.size && (
                            <p>{(image.size / 1024 / 1024).toFixed(1)} MB</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Metadata</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Created:</span>
                      <span>{formatDate(property?.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Created by:</span>
                      <span>{property?.created_by || "N/A"}</span>
                    </div>
                    {property?.updated_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Updated:</span>
                        <span>{formatDate(property.updated_at)}</span>
                      </div>
                    )}
                    {property?.updated_by && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          Updated by:
                        </span>
                        <span>{property.updated_by}</span>
                      </div>
                    )}
                  </div>

                  {propertyArea && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Area Information</h4>
                      <p className="text-sm">
                        <strong>{propertyArea.area_name}</strong>
                        <br />
                        {propertyArea.province}, {propertyArea.country}
                      </p>
                    </div>
                  )}

                  {propertyWithRelations?.propertyType && (
                    <div className="mt-4">
                      <Badge variant="outline">
                        {propertyWithRelations.propertyType
                          .replace(/_/g, " ")
                          .toUpperCase()}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : mode === "create"
                  ? "Create Property"
                  : "Save Changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* image gallery modal */}
      <Dialog
        open={selectedImageIndex !== null}
        onOpenChange={() => setSelectedImageIndex(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 z-10 bg-black/20 hover:bg-black/40 text-white"
              onClick={() => setSelectedImageIndex(null)}
            >
              <X className="h-4 w-4" />
            </Button>

            {selectedImageIndex !== null &&
              property?.images?.[selectedImageIndex] && (
                <>
                  <div className="flex items-center justify-center bg-black">
                    <ImageDisplay
                      imageKey={property.images[selectedImageIndex].key}
                      alt={property.images[selectedImageIndex].filename}
                      className="max-h-[80vh] max-w-full object-contain"
                    />
                  </div>

                  {/* navigation buttons */}
                  {property.images.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white"
                        onClick={() =>
                          setSelectedImageIndex(
                            selectedImageIndex > 0
                              ? selectedImageIndex - 1
                              : property.images.length - 1
                          )
                        }
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white"
                        onClick={() =>
                          setSelectedImageIndex(
                            selectedImageIndex < property.images.length - 1
                              ? selectedImageIndex + 1
                              : 0
                          )
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}

                  {/* image info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {property.images[selectedImageIndex].filename}
                        </p>
                        {property.images[selectedImageIndex].size && (
                          <p className="text-sm text-gray-300">
                            {(
                              property.images[selectedImageIndex].size /
                              1024 /
                              1024
                            ).toFixed(1)}{" "}
                            MB
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-gray-300">
                        {selectedImageIndex + 1} of {property.images.length}
                      </div>
                    </div>
                  </div>
                </>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
